import { LIMIT } from '@/constants';
import localConf from '@/local-conf';
import {
  fetchGetActiveFirstCustomerBenefit,
  fetchGetUserMember,
  fetchGetUserVirtualCurrencyBalance,
  fetchMaxDiscountMembershipPlan,
  fetchMemberCardDetail,
  fetchMemberCardList,
  fetchMemberGiftCard,
  fetchMembershipOrders,
  // fetchPurchaseMembership,
  fetchUserInfo,
  fetchUserMemberGiftCardOrders,
  uniPayment,
} from '@/services';
import { LoadMoreStatus, MemberState, type AnyObj, type AnyObjOrNull } from '@/types';
import { commonUtils } from '@/utils';
import NP from 'number-precision';
import { defineStore } from 'pinia';

interface State {
  /** 用户信息 */
  userInfo: AnyObjOrNull;
  /** 用户会员信息 */
  userMemberInfo: AnyObjOrNull;
  /** 会员卡列表数据 */
  memberCardList: AnyObj[];
  /** 会员卡详情数据 */
  memberCardDetail: AnyObjOrNull;
  /** 最大优惠的会员卡数据 */
  maxDiscountMembershipPlan: AnyObjOrNull;
  /** 当前选择的会员下标 */
  swiperCurrentIndex: number;
  /** 是否展示会员卡详情标识 */
  displayMemberCardDetailFlag: boolean;
  /** 购卡记录数据配置 */
  purchaseRecordConfig: {
    list: AnyObj[];
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: { skip: number };
  };
  /** 购卡记录数据配置 */
  giftCardRecordConfig: {
    list: AnyObj[];
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: { skip: number };
  };
  /** 礼品卡数据 */
  giftCardData: AnyObjOrNull;
  /** 新客指引优惠券是否失效 */
  activeFirstCustomerBenefit: AnyObject | null;
  isAuthNewInvalidCoupon: boolean;
  /** 虚拟币余额 */
  virtualCurrencyBalance: number;
}

export const useUserStore = defineStore('user', {
  state: (): State => ({
    userInfo: null,
    userMemberInfo: null,
    memberCardList: [],
    memberCardDetail: null,
    maxDiscountMembershipPlan: null,
    swiperCurrentIndex: 0,
    displayMemberCardDetailFlag: false,
    purchaseRecordConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: { skip: 0 },
    },
    giftCardRecordConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: { skip: 0 },
    },
    giftCardData: null,
    activeFirstCustomerBenefit: null,
    isAuthNewInvalidCoupon: false,
    virtualCurrencyBalance: 0,
  }),

  getters: {
    /** 是否会员且未过期状态 */
    isMember(state) {
      return !!state.userMemberInfo && state.userMemberInfo.state === MemberState.normal;
    },
    /** 会员列表数据项 */
    memberItem(state) {
      return state.displayMemberCardDetailFlag
        ? state.memberCardDetail
        : state.memberCardList[state.swiperCurrentIndex];
    },
    tabConfigs() {
      const config: AnyObj[] = [];

      const item = this.memberItem;

      if (!item) return config;

      if (item.rightsDiscount?.enable) {
        config.push({
          data: item.rightsDiscount,
          text: '打折',
          description: `下单享受打折优惠${NP.divide(item.rightsDiscount?.discountRate ?? 0, 10)}折`,
          imgUrl: commonUtils.img.remote('vip_discount'),
          imgUrlNormal: commonUtils.img.remote('privilege_discount_noraml'),
          imgUrlActive: commonUtils.img.remote('privilege_discount_active'),
        });
      }
      if (item.rightsCoupon?.enable) {
        let descriptionArrayText = ``;
        item.membershipPlanCoupon.forEach((it: AnyObj, index: number) => {
          descriptionArrayText += `${it.coupon?.name}（${it.quantity}张）${
            index === item.membershipPlanCoupon.length - 1 ? '' : '，'
          }`;
        });
        config.push({
          data: item.membershipPlanCoupon,
          text: '送优惠券',
          description: `随卡赠送${descriptionArrayText}`,
          imgUrl: commonUtils.img.remote('vip_coupon'),
          imgUrlNormal: commonUtils.img.remote('privilege_coupon_noraml'),
          imgUrlActive: commonUtils.img.remote('privilege_coupon_active'),
        });
      }
      if (item.rightsPoints?.enable) {
        config.push({
          data: item.rightsPoints,
          text: '积分倍率',
          description: `赠送多倍积分${item.rightsPoints?.pointsMultiple ?? 1}倍`,
          imgUrl: commonUtils.img.remote('vip_integral'),
          imgUrlNormal: commonUtils.img.remote('privilege_intrgral_noraml'),
          imgUrlActive: commonUtils.img.remote('privilege_integral_active'),
        });
      }

      // 配合布局
      while (config.length < 4) {
        config.push({ data: null });
      }

      return config;
    },
    // 联系客服时传入的session-from
    sessionFrom(state) {
      const nickName = state.userInfo?.lastName ?? '微信用户';
      const avatarUrl = state.userInfo?.customFields?.headPortrait ?? '';
      const config = JSON.stringify({
        data: JSON.stringify([
          {
            type: 'crm_param',
            key: 'channelToken',
            value: localConf.channel,
          },
        ]),
      });
      const res = `nickName=${nickName}|avatarUrl=${avatarUrl}|ysf.config=${config}`;
      return res;
    },
  },

  actions: {
    async getMemberCardList(refresh?: boolean) {
      if (!this.memberCardList.length || refresh) {
        const data = await fetchMemberCardList();
        if (data) this.memberCardList = data;
      }
      return this.memberCardList;
    },
    async getMemberCardDetail(membershipPlanId: string, refresh?: boolean) {
      if (!this.memberCardDetail || refresh) {
        const data = await fetchMemberCardDetail(membershipPlanId);
        if (data) this.memberCardDetail = data;
      }
      return this.memberCardDetail;
    },
    async getMemberGiftCardDetail(giftCardId: string, refresh?: boolean) {
      if (!this.giftCardData || refresh) {
        const data = await fetchMemberGiftCard(giftCardId);
        if (data) this.giftCardData = data;
      }
      return this.giftCardData;
    },
    async getMaxDiscountMembershipPlan(refresh?: boolean) {
      if (!this.maxDiscountMembershipPlan || refresh) {
        const data = await fetchMaxDiscountMembershipPlan();
        if (data) this.maxDiscountMembershipPlan = data;
      }
      return this.maxDiscountMembershipPlan;
    },
    async getUserInfo(refresh?: boolean) {
      if (!this.userInfo || refresh) {
        const data = await fetchUserInfo();
        this.userInfo = data;
      }
      return this.userInfo;
    },
    async getUserMemberInfo(refresh?: boolean) {
      if (!this.userMemberInfo || refresh) {
        const data = await fetchGetUserMember();
        if (data) this.userMemberInfo = data;
      }
      return this.userMemberInfo;
    },
    // 获取用户虚拟币余额
    async getVirtualCurrencyBalance(refresh?: boolean) {
      if (!this.virtualCurrencyBalance || refresh) {
        const data = await fetchGetUserVirtualCurrencyBalance();
        if (data || data === 0) this.virtualCurrencyBalance = NP.divide(data, 100);
      }
      return this.virtualCurrencyBalance;
    },
    async getPurchaseRecordList(skip: number = 0, refresh?: boolean) {
      const res = await fetchMembershipOrders(skip);
      if (res) {
        if (res.length < LIMIT) {
          this.purchaseRecordConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.purchaseRecordConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.purchaseRecordConfig.list = res;
        } else {
          this.purchaseRecordConfig.list = [...this.purchaseRecordConfig.list, ...res];
        }
      }
      return this.purchaseRecordConfig.list;
    },
    async getGiftCardRecordList(skip: number = 0, refresh?: boolean) {
      const res = await fetchUserMemberGiftCardOrders(skip);
      if (res) {
        if (res.length < LIMIT) {
          this.giftCardRecordConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.giftCardRecordConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.giftCardRecordConfig.list = res;
        } else {
          this.giftCardRecordConfig.list = [...this.giftCardRecordConfig.list, ...res];
        }
      }
      return this.giftCardRecordConfig.list;
    },
    async getActiveFirstCustomerBenefit(refresh?: boolean) {
      try {
        if (!this.activeFirstCustomerBenefit || refresh) {
          const data = await fetchGetActiveFirstCustomerBenefit();
          if (data) this.activeFirstCustomerBenefit = data;
          return this.activeFirstCustomerBenefit;
        }
      } catch (error) {
        console.log(error);
      }
    },
    authUserInvalidModal(confirm: () => void) {
      let that = this;
      return uni.showModal({
        title: '提示',
        content: '礼包已失效',
        confirmText: '继续授权',
        cancelText: '去逛逛',
        success(res) {
          if (res.confirm) {
            that.isAuthNewInvalidCoupon = true;
            confirm?.();
          } else {
            uni.navigateBack();
          }
        },
      });
    },
    resetHandler() {
      this.$state = {
        ...this.$state,
        memberCardList: [],
        memberCardDetail: null,
        swiperCurrentIndex: 0,
        displayMemberCardDetailFlag: false,
      };
    },
  },
});
